import { visitAtIsoToDateInput } from "@/components/handshake/visitAt";
import type { BookingListRow } from "./bookingViewModel";
import { totalGuests } from "./bookingDayAgenda";

/** `YYYY-MM-DD` in the activity local timezone; null when visit_at is missing. */
export type CalendarDateKey = string;

export interface CalendarMonth {
  year: number;
  /** 0-based month index */
  month: number;
}

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDateKey(value: string): value is CalendarDateKey {
  return DATE_KEY_RE.test(value);
}

export function getBookingCalendarDateKey(
  booking: BookingListRow,
): CalendarDateKey | null {
  if (!booking.visitAt) return null;
  return visitAtIsoToDateInput(booking.visitAt, booking.activityRegionCode);
}

/** Bookings and guests on one calendar day. */
export interface CalendarDayTotals {
  bookings: number;
  guests: number;
}

export interface BookingDaySection {
  dateKey: CalendarDateKey | null;
  bookings: BookingListRow[];
}

/**
 * Day sections ordered by visit date.
 * Active list: soonest day first. Past list: most recent day first.
 * Bookings without `visitAt` go in a trailing undated section.
 */
export function buildOrderedDaySections(
  bookings: BookingListRow[],
  options: { pastFirst: boolean },
): BookingDaySection[] {
  const byDate = groupBookingsByCalendarDate(bookings);
  const datedKeys = [...byDate.keys()].sort((a, b) =>
    options.pastFirst ? b.localeCompare(a) : a.localeCompare(b),
  );

  const sections: BookingDaySection[] = datedKeys.map((dateKey) => ({
    dateKey,
    bookings: byDate.get(dateKey)!,
  }));

  const undated = bookings.filter((b) => !getBookingCalendarDateKey(b));
  if (undated.length > 0) {
    sections.push({ dateKey: null, bookings: undated });
  }

  return sections;
}

/** Pack whole day sections into pages without splitting a day across pages. */
export function paginateDaySections(
  sections: BookingDaySection[],
  page: number,
  maxItems: number,
): { pageSections: BookingDaySection[]; totalPages: number } {
  if (sections.length === 0) {
    return { pageSections: [], totalPages: 1 };
  }

  const pages: BookingDaySection[][] = [];
  let current: BookingDaySection[] = [];
  let count = 0;

  for (const section of sections) {
    const n = section.bookings.length;
    if (current.length > 0 && count + n > maxItems) {
      pages.push(current);
      current = [];
      count = 0;
    }
    current.push(section);
    count += n;
  }
  if (current.length > 0) {
    pages.push(current);
  }

  const totalPages = Math.max(1, pages.length);
  const safePage = Math.min(Math.max(1, page), totalPages);
  return {
    pageSections: pages[safePage - 1] ?? [],
    totalPages,
  };
}

export function groupBookingsByCalendarDate(
  bookings: BookingListRow[],
): Map<CalendarDateKey, BookingListRow[]> {
  const map = new Map<CalendarDateKey, BookingListRow[]>();

  for (const booking of bookings) {
    const key = getBookingCalendarDateKey(booking);
    if (!key) continue;
    const list = map.get(key);
    if (list) {
      list.push(booking);
    } else {
      map.set(key, [booking]);
    }
  }

  for (const list of map.values()) {
    list.sort((a, b) => {
      const ta = new Date(a.visitAt).getTime();
      const tb = new Date(b.visitAt).getTime();
      return ta - tb;
    });
  }

  return map;
}

/** Day totals from already-loaded rows (customer list, which is not server-paged). */
export function calendarDayTotalsFromRows(
  rows: BookingListRow[],
): Map<CalendarDateKey, CalendarDayTotals> {
  const totals = new Map<CalendarDateKey, CalendarDayTotals>();
  for (const [dateKey, bookings] of groupBookingsByCalendarDate(rows)) {
    totals.set(dateKey, {
      bookings: bookings.length,
      guests: totalGuests(bookings),
    });
  }
  return totals;
}

export function parseCalendarDateKey(key: CalendarDateKey): CalendarMonth & {
  day: number;
} {
  const [y, m, d] = key.split("-").map(Number);
  return { year: y, month: m - 1, day: d };
}

/** Localized weekday + civil date for activity-local day section headers. */
export function formatCalendarDateHeading(
  dateKey: CalendarDateKey,
  locale: string,
): string {
  const { year, month, day } = parseCalendarDateKey(dateKey);
  return new Date(Date.UTC(year, month, day)).toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/** Browser-local today; aligns calendar chrome with seller-local "today". */
export function todayCalendarDateKey(): CalendarDateKey {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateKey(year: number, month: number, day: number): CalendarDateKey {
  const m = String(month + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

export function addDaysToDateKey(
  dateKey: CalendarDateKey,
  delta: number,
): CalendarDateKey {
  const { year, month, day } = parseCalendarDateKey(dateKey);
  const d = new Date(Date.UTC(year, month, day + delta));
  return formatDateKey(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Monday of the ISO-style week that contains `dateKey` (UTC). */
export function weekStartFromDateKey(dateKey: CalendarDateKey): CalendarDateKey {
  const { year, month, day } = parseCalendarDateKey(dateKey);
  const d = new Date(Date.UTC(year, month, day));
  const mondayOffset = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - mondayOffset);
  return formatDateKey(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function buildWeekDateKeys(
  weekStart: CalendarDateKey,
): CalendarDateKey[] {
  return Array.from({ length: 7 }, (_, i) => addDaysToDateKey(weekStart, i));
}
